import React, { useState, useEffect } from "react";
import {
  Drawer,
  Box,
  TextField,
  Button,
  IconButton,
  Typography,
  Grid,
  CircularProgress,
  Avatar,
  Badge,
  Divider,
  Stack,
} from "@mui/material";
import {
  Close as CloseIcon,
  CameraAlt as CameraIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import FileUploadSection from "../fileuploadsection";
import { useFileUpload } from "../../../../hooks/useFileUpload";
import { FileService } from "../../../../services/staffandplant/plantService";
import ImagePreviewModal from "./imagepreviewmodal";

const PlantFormDialog = ({
  open,
  onClose,
  title,
  formData,
  onFieldChange,
  onDateChange,
  onDocumentsChange,
  onSubmit,
  loading,
  isValid,
}) => {
  const { uploadSingleFile, uploading: profileUploading } = useFileUpload();
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [profileImageName, setProfileImageName] = useState(formData.profile_image?.name || "");
  const [errors, setErrors] = useState({});
  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  const validateField = (field, value) => {
    if (
      ["nickname", "plant_id", "registration_number", "date_on_site"].includes(
        field
      ) &&
      !value
    ) {
      return "This field is required";
    }
    return "";
  };

  const handleFieldChange = (field, value) => {
    onFieldChange(field, value);
    setErrors((prev) => ({ ...prev, [field]: validateField(field, value) }));
  };

  const handleProfileImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert("Please select an image file");
      return;
    }

    console.log("Starting profile image upload for:", file.name);
    console.log("File details:", {
      name: file.name,
      size: file.size,
      type: file.type,
    });

    try {
      // Create blob URL for immediate preview
      const blobUrl = URL.createObjectURL(file);
      setProfileImagePreview(blobUrl);
      setProfileImageFile(file);

      // Set profile image name to file name by default on new upload
      setProfileImageName(file.name);

      // Store file reference in form data for later upload
      onFieldChange("profile_image_file", file);

      console.log("Profile image selected successfully:", file.name);
      console.log("Blob URL created:", blobUrl);
    } catch (error) {
      console.error("Error handling profile image:", error);
      alert("Failed to load profile image preview");
    }
  };

  const handleRemoveProfileImage = () => {
    if (profileImagePreview && profileImagePreview.startsWith("blob:")) {
      URL.revokeObjectURL(profileImagePreview);
    }
    setProfileImagePreview(null);
    setProfileImageFile(null);
    onFieldChange("profile_image", null);
    onFieldChange("profile_image_file", null);

    const fileInput = document.getElementById("profile-image-upload");
    if (fileInput) fileInput.value = "";
  };

  const handleProfileImageClick = () => {
    if (formData.profile_image?.id) {
      setPreviewModalOpen(true);
    }
  };

  const handleSubmit = async () => {
    try {
      let updatedFormData = { ...formData };

      // If there's a new profile image file, upload it first
      if (profileImageFile) {
        console.log(
          "Uploading profile image before form submission:",
          profileImageFile.name
        );
        console.log("Profile image file details:", {
          name: profileImageFile.name,
          size: profileImageFile.size,
          type: profileImageFile.type,
        });

        try {
          // Use the renamed profileImageName for upload if changed
          const uploadResult = await uploadSingleFile(
            new File([profileImageFile], profileImageName, {
              type: profileImageFile.type,
            }),
            true
          );
          console.log("Profile image upload successful. Result:", uploadResult);

          if (uploadResult?.id) {
            updatedFormData.profile_image = {
              id: uploadResult.id,
              name: profileImageName,
              type: uploadResult.type || 1,
            };

            console.log(
              "Updated form data with profile image:",
              updatedFormData.profile_image
            );

            // Update the form data with the uploaded image info
            onFieldChange("profile_image", updatedFormData.profile_image);
          } else {
            console.error("Upload result missing ID:", uploadResult);
            alert("Profile image upload failed - no ID returned");
            return;
          }
        } catch (error) {
          console.error("Error uploading profile image:", error);
          console.error("Upload error details:", {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
          });
          alert(`Failed to upload profile image: ${error.message}`);
          return;
        }
      }

      console.log(
        "Calling original submit handler with updated form data:",
        updatedFormData
      );
      // Call the original submit handler with updated form data
      await onSubmit(updatedFormData);
    } catch (error) {
      console.error("Error in form submission:", error);
      alert(`Form submission failed: ${error.message}`);
    }
  };

  useEffect(() => {
    const loadExistingImage = async () => {
      if (formData.profile_image?.id && !profileImagePreview) {
        try {
          const imageUrl = await FileService.getFileBlob(
            formData.profile_image.id,
            true
          );
          if (imageUrl) {
            setProfileImagePreview(imageUrl);
          }
        } catch (error) {
          console.error("Error loading existing profile image:", error);
        }
      }
    };

    if (open) {
      loadExistingImage();
    } else {
      // Cleanup blob URLs when drawer closes
      if (profileImagePreview && profileImagePreview.startsWith("blob:")) {
        URL.revokeObjectURL(profileImagePreview);
      }
      setProfileImagePreview(null);
      setProfileImageFile(null);
    }

    // Cleanup on unmount
    return () => {
      if (profileImagePreview && profileImagePreview.startsWith("blob:")) {
        URL.revokeObjectURL(profileImagePreview);
      }
    };
    // eslint-disable-next-line
  }, [formData.profile_image, open]);

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        sx={{
          "& .MuiDrawer-paper": {
            width: { xs: "100vw", sm: "500px", md: "600px" },
            border: "none",
          },
        }}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          {/* Header */}
          <Box sx={{ p: 3, borderBottom: "1px solid #e0e0e0" }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="h6" sx={{ fontWeight: 600, color: "#333" }}>
                {title}
              </Typography>
              <IconButton onClick={onClose} size="small">
                <CloseIcon />
              </IconButton>
            </Stack>
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
            <Stack spacing={3}>
              {/* Profile Image Section */}
              <Box display="flex" justifyContent="center">
                <Box sx={{ position: "relative" }}>
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            badgeContent={
              <Box>
                <input
                  id="profile-image-upload"
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleProfileImageUpload}
                  disabled={profileUploading}
                />
                <IconButton
                  component="label"
                  htmlFor="profile-image-upload"
                  size="small"
                  sx={{
                    bgcolor: "#666",
                    color: "white",
                    "&:hover": { bgcolor: "#333" },
                    width: 36,
                    height: 36,
                    border: "2px solid white",
                  }}
                  disabled={profileUploading}
                >
                  {profileUploading ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <CameraIcon fontSize="small" />
                  )}
                </IconButton>
              </Box>
            }
          >
            <Avatar
              sx={{
                width: 120,
                height: 120,
                bgcolor: profileImagePreview
                  ? "transparent"
                  : "#f8f9fa",
                border: "2px solid #e0e0e0",
                cursor: formData.profile_image?.id
                  ? "pointer"
                  : "default",
              }}
              src="./Profile.jpg"
              onClick={handleProfileImageClick}
            >
              {!profileImagePreview && (
                <img
                  src="/assets/plant-icon.svg"
                  alt="Plant"
                  width={60}
                  height={60}
                />
              )}
            </Avatar>
          </Badge>
          {(formData.profile_image || profileImageFile) && (
            <Box sx={{ mt: 1, textAlign: "center" }}>
              <TextField
                label="Image Name"
                value={profileImageName}
                onChange={(e) => setProfileImageName(e.target.value)}
                size="small"
                variant="outlined"
                sx={{ width: "100%" }}
                disabled={profileUploading}
              />
              <IconButton
                size="small"
                onClick={handleRemoveProfileImage}
                sx={{
                  position: "absolute",
                  top: -8,
                  right: -8,
                  bgcolor: "#dc3545",
                  color: "white",
                  "&:hover": { bgcolor: "#c82333" },
                  width: 28,
                  height: 28,
                  border: "2px solid white",
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
                </Box>
              </Box>

              <Divider />

              {/* Form Fields */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="nickname"
                    label="Nickname"
                    fullWidth
                    value={formData.nickname || ""}
                    onChange={(e) =>
                      handleFieldChange("nickname", e.target.value)
                    }
                    required
                    placeholder="Enter nickname"
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.nickname}
                    helperText={errors.nickname}
                    variant="outlined"
                    size="medium"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="plant_id"
                    label="Plant ID"
                    fullWidth
                    value={formData.plant_id || ""}
                    onChange={(e) =>
                      handleFieldChange("plant_id", e.target.value)
                    }
                    required
                    placeholder="Enter plant ID"
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.plant_id}
                    helperText={errors.plant_id}
                    variant="outlined"
                    size="medium"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="plant_type"
                    label="Plant Type"
                    fullWidth
                    value={formData.plant_type || ""}
                    onChange={(e) =>
                      handleFieldChange("plant_type", e.target.value)
                    }
                    placeholder="Enter plant type"
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    size="medium"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="make"
                    label="Make"
                    fullWidth
                    value={formData.make || ""}
                    onChange={(e) => handleFieldChange("make", e.target.value)}
                    placeholder="Enter make"
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    size="medium"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="model"
                    label="Model"
                    fullWidth
                    value={formData.model || ""}
                    onChange={(e) => handleFieldChange("model", e.target.value)}
                    placeholder="Enter model"
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    size="medium"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="registration_number"
                    label="Registration Number"
                    fullWidth
                    value={formData.registration_number || ""}
                    onChange={(e) =>
                      handleFieldChange("registration_number", e.target.value)
                    }
                    required
                    placeholder="Enter registration number"
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.registration_number}
                    helperText={errors.registration_number}
                    variant="outlined"
                    size="medium"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Date on site *"
                      value={formData.date_on_site || null}
                      onChange={(date) => {
                        onDateChange(date);
                        setErrors((prev) => ({
                          ...prev,
                          date_on_site: validateField("date_on_site", date),
                        }));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          required
                          placeholder="Select date"
                          InputLabelProps={{ shrink: true }}
                          error={!!errors.date_on_site}
                          helperText={errors.date_on_site}
                          variant="outlined"
                          size="medium"
                        />
                      )}
                    />
                  </LocalizationProvider>
                </Grid>
              </Grid>

              <Divider />

              {/* File Upload Section */}
              <FileUploadSection
                documents={formData.document || []}
                onDocumentsChange={onDocumentsChange}
                disabled={loading}
              />
            </Stack>
          </Box>

          {/* Footer Actions */}
          <Box sx={{ p: 3, borderTop: "1px solid #e0e0e0" }}>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                onClick={onClose}
                variant="outlined"
                sx={{
                  borderColor: "#666",
                  color: "#666",
                  "&:hover": {
                    borderColor: "#333",
                    color: "#333",
                  },
                  minWidth: 100,
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                variant="contained"
                disabled={
                  loading ||
                  !isValid ||
                  profileUploading ||
                  Object.values(errors).some((e) => e)
                }
                sx={{
                  bgcolor: "#333",
                  "&:hover": { bgcolor: "#000" },
                  "&:disabled": {
                    bgcolor: "#999",
                    color: "white",
                    opacity: 0.7,
                  },
                  minWidth: 100,
                }}
              >
                {loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Save"
                )}
              </Button>
            </Stack>
          </Box>
        </Box>
      </Drawer>

      {/* Profile Image Preview Modal */}
      <ImagePreviewModal
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        fileId={formData.profile_image?.id}
        fileName={formData.profile_image?.name || "Profile Image"}
        isOrg={true}
      />
    </>
  );
};

export default PlantFormDialog;
